import { Trans } from '@lingui/macro'
import { Currency, CurrencyAmount } from '@uniswap/sdk-core'
import { LoadingRows } from 'components/Loader/styled'
import { NetworkAlert } from 'components/NetworkAlert/NetworkAlert'
import StakeHeader from 'components/Stake/StakeHeader'
import StakeInputPanel from 'components/StakeInputPanel'
import { MouseoverTooltip } from 'components/Tooltip'
import { STAKING_ADDRESS } from 'constants/addresses'
import { SupportedChainId } from 'constants/chains'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import useChadBalance from 'hooks/useChadBalance'
import { useStakeCallback } from 'hooks/useStakeCallback'
import JSBI from 'jsbi'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { CheckCircle, HelpCircle } from 'react-feather'
import { RouteComponentProps } from 'react-router-dom'
import { Text } from 'rebass'
import { useDerivedStakeInfo, useStakeActionHandlers } from 'state/staking/hooks'
import styled, { ThemeContext } from 'styled-components/macro'
import { ThemedText } from 'theme'

import { BorrowCallbackError, Wrapper } from '../../components/Borrow/styleds'
import { ButtonConfirmed, ButtonError, ButtonLight } from '../../components/Button'
import { AutoColumn } from '../../components/Column'
import Loader from '../../components/Loader'
import { AutoRow } from '../../components/Row'
import { UUSD } from '../../constants/tokens'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import { useWalletModalToggle } from '../../state/application/hooks'
import { useExpertModeManager } from '../../state/user/hooks'
import AppBody from '../AppBody'

const AlertWrapper = styled.div`
  max-width: 460px;
  width: 100%;
`
const HoverText = styled(ThemedText.Main)`
  text-decoration: none;
  color: ${({ theme }) => theme.text3};
  :hover {
    color: ${({ theme }) => theme.text1};
    text-decoration: none;
  }
`

export default function Staking({
  match: {
    params: { tokenId: tokenIdFromUrl },
  },
  history,
}: RouteComponentProps<{ tokenId?: string }>) {
  const { account, chainId } = useActiveWeb3React()

  const chadBalance = useChadBalance(account ?? undefined)

  const maxStakeBalance = useMemo(() => {
    return chadBalance || CurrencyAmount.fromRawAmount(UUSD[chainId ? chainId : 1], '0')
  }, [chadBalance, chainId])

  const currency = UUSD[chainId ? chainId : 1]
  const { parsedAmount, inputError: borrowInputError } = useDerivedStakeInfo()

  const theme = useContext(ThemeContext)

  // toggle wallet when disconnected
  const toggleWalletModal = useWalletModalToggle()

  // for expert mode
  const [isExpertMode] = useExpertModeManager()

  const { onUserInput } = useStakeActionHandlers()
  const isValid =
    parsedAmount?.greaterThan(JSBI.BigInt(0)) &&
    (parsedAmount.lessThan(maxStakeBalance) || parsedAmount.equalTo(maxStakeBalance)) &&
    maxStakeBalance.greaterThan(JSBI.BigInt(0))

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(value)
    },
    [onUserInput]
  )

  const [{ showConfirm, borrowToConfirm, borrowErrorMessage, attemptingTxn, txHash }, setBorrowState] = useState<{
    showConfirm: boolean
    borrowToConfirm: CurrencyAmount<Currency> | undefined
    attemptingTxn: boolean
    borrowErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    showConfirm: false,
    borrowToConfirm: CurrencyAmount.fromRawAmount(UUSD[chainId ? chainId : 1], JSBI.BigInt(0)),
    attemptingTxn: false,
    borrowErrorMessage: undefined,
    txHash: undefined,
  })

  // check whether the user has approved the router on the input token
  const [approvalState, approveCallback] = useApproveCallback(
    parsedAmount,
    STAKING_ADDRESS[chainId || SupportedChainId.SONIC]
  )

  const handleApprove = useCallback(async () => {
    await approveCallback()
  }, [approveCallback])

  // check if user has gone through approval process, used to show two step buttons, reset on token change
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

  // mark when a user has submitted an approval, reset onTokenSelection for input field
  useEffect(() => {
    if (approvalState === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approvalState, approvalSubmitted])

  const maxInputAmount: CurrencyAmount<Currency> | undefined = useMemo(() => maxStakeBalance, [maxStakeBalance])

  const showMaxButton = Boolean(maxStakeBalance?.greaterThan(0) && !parsedAmount?.equalTo(maxStakeBalance))

  const { state: borrowState, callback: borrowCallback } = useStakeCallback(parsedAmount)

  const handleBorrow = useCallback(async () => {
    if (!borrowCallback || !parsedAmount?.greaterThan(JSBI.BigInt(0))) {
      return
    }

    setBorrowState({
      attemptingTxn: true,
      borrowToConfirm: parsedAmount,
      showConfirm,
      borrowErrorMessage: undefined,
      txHash: undefined,
    })

    borrowCallback()
      .then((hash) => {
        setBorrowState({
          attemptingTxn: false,
          borrowToConfirm: parsedAmount,
          showConfirm,
          borrowErrorMessage: undefined,
          txHash: hash,
        })
      })
      .catch((error) => {
        setBorrowState({
          attemptingTxn: false,
          borrowToConfirm: parsedAmount,
          showConfirm,
          borrowErrorMessage: error.message,
          txHash: undefined,
        })
      })
  }, [borrowCallback, parsedAmount, showConfirm, account])
  const showApproveFlow =
    approvalState === ApprovalState.NOT_APPROVED ||
    approvalState === ApprovalState.PENDING ||
    (approvalSubmitted && approvalState === ApprovalState.APPROVED)

  const handleMaxInput = useCallback(() => {
    maxInputAmount && onUserInput(maxStakeBalance.toExact())
  }, [maxInputAmount, onUserInput])

  return (
    <>
      <AppBody>
        <StakeHeader />
        <Wrapper id="stake-page">
          {Boolean(!chadBalance) ? (
            <LoadingRows style={{ gap: '8px' }}>
              <div />
              <div />
              <div />
            </LoadingRows>
          ) : (
            <AutoColumn gap={'sm'}>
              <div style={{ display: 'relative' }}>
                <StakeInputPanel
                  label={<Trans>Stake</Trans>}
                  value={parsedAmount?.toExact() || ''}
                  showMaxButton={showMaxButton}
                  currency={currency}
                  onUserInput={handleTypeInput}
                  onMax={handleMaxInput}
                  showCommonBases={true}
                  id="stake-input"
                  loading={!chadBalance}
                  maxStakeBalance={maxStakeBalance}
                />
              </div>

              <div>
                {!account ? (
                  <ButtonLight onClick={toggleWalletModal}>
                    <Trans>Connect Wallet</Trans>
                  </ButtonLight>
                ) : showApproveFlow ? (
                  <AutoRow style={{ flexWrap: 'nowrap', width: '100%' }}>
                    <AutoColumn style={{ width: '100%' }} gap="12px">
                      <ButtonConfirmed
                        onClick={handleApprove}
                        disabled={approvalState !== ApprovalState.NOT_APPROVED || approvalSubmitted}
                        width="100%"
                        altDisabledStyle={approvalState === ApprovalState.PENDING} // show solid button while waiting
                        confirmed={approvalState === ApprovalState.APPROVED}
                      >
                        <AutoRow justify="space-between" style={{ flexWrap: 'nowrap' }}>
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            {/* we need to shorten this string on mobile */}
                            {approvalState === ApprovalState.APPROVED ? (
                              <Trans>You can now stake your CUSD</Trans>
                            ) : (
                              <Trans>Allow the Chad Finance Protocol to use your Chad USD</Trans>
                            )}
                          </span>
                          {approvalState === ApprovalState.PENDING ? (
                            <Loader stroke="white" />
                          ) : approvalSubmitted && approvalState === ApprovalState.APPROVED ? (
                            <CheckCircle size="20" color={theme.green1} />
                          ) : (
                            <MouseoverTooltip
                              text={
                                <Trans>You must give the Stake smart contracts permission to use your Chad USD.</Trans>
                              }
                            >
                              <HelpCircle size="20" color={'white'} style={{ marginLeft: '8px' }} />
                            </MouseoverTooltip>
                          )}
                        </AutoRow>
                      </ButtonConfirmed>
                      <ButtonError
                        onClick={handleBorrow}
                        width="100%"
                        id="borrow-button"
                        disabled={!isValid || !chadBalance || approvalState !== ApprovalState.APPROVED}
                        error={isValid}
                      >
                        <Text fontSize={16} fontWeight={500}>
                          <Trans>Stake</Trans>
                        </Text>
                      </ButtonError>
                    </AutoColumn>
                  </AutoRow>
                ) : (
                  <ButtonError
                    onClick={handleBorrow}
                    id="borrow-button"
                    disabled={!isValid || !chadBalance}
                    error={isValid}
                  >
                    <Text fontSize={20} fontWeight={500}>
                      {borrowInputError ? (
                        borrowInputError
                      ) : !chadBalance ? (
                        <Trans>Loading Balance</Trans>
                      ) : (
                        <Trans>Stake</Trans>
                      )}
                    </Text>
                  </ButtonError>
                )}
                {isExpertMode && borrowErrorMessage ? <BorrowCallbackError error={borrowErrorMessage} /> : null}
              </div>
            </AutoColumn>
          )}
        </Wrapper>
      </AppBody>
      <AlertWrapper>
        <NetworkAlert />
      </AlertWrapper>
    </>
  )
}
